<template>
  <div
    v-if="experiences.length"
    id="experience"
    class="experience-feed"
  >
    <h2
      class="feed-header obs-el"
      data-attr="experience"
    >
      Экскурсии
    </h2>
    <div class="feed-list">
      <div
        v-for="(exp, index) in localizedExperiences"
        :key="exp.id ?? index"
        v-exp-view-tracker="expAnalytics(exp)"
        class="feed-card"
        @click="onCardClick(exp, $event)"
      >
        <ExpCard
          :exp="exp"
          size="s"
          :is-in-favorites="isFavorite(exp.id)"
          style="width: 100%"
          @toggle-favorite="onToggleFavorite(exp)"
        />
      </div>
    </div>
    <div
      v-if="hasMore"
      class="feed-actions-wrap"
    >
      <div
        class="feed-actions"
        :class="{ 'is-stuck': isStuck }"
      >
        <UiButtonV3
          class="show-more-button"
          is-full-width
          :variant="UiButtonVariant.PRIMARY"
          :color-mode="UiButtonColorMode.NEUTRAL"
          :text="btnText"
          @clicked="onShowAll"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ExpCard, type ExpCardProps } from '@experience/exp-card';
  import { UiButtonV3, UiButtonColorMode, UiButtonVariant } from '@experience/vue3-kit';
  import { computed, inject, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

  import { useFavorites } from '@/shared/composables/useFavorites';
  import { useMovementTypes } from '@/shared/composables/useMovementTypes';
  import { trackEvent, useViewTracker } from '@/shared/lib/analytics/compat';

  type Experience = ExpCardProps['exp'];

  const { isFavorite, seedFavorites, toggleFavorite } = useFavorites();
  const { loadMovementTypes, localizeExperienceMovement } = useMovementTypes();

  onMounted(loadMovementTypes);

  const props = withDefaults(
    defineProps<{
      experiences: Experience[];
      hasMore?: boolean;
      totalCount?: number;
      showAllUrl?: string;
      analyticsBase?: Record<string, unknown>;
    }>(),
    {
      hasMore: false,
      totalCount: 0,
      showAllUrl: '',
      analyticsBase: () => ({}),
    },
  );

  const emit = defineEmits<{
    'show-all': [];
  }>();

  const vExpViewTracker = useViewTracker('around:sights:sight-popup:experience.view');

  // Формирует набор параметров аналитики по экспириенсу
  const expAnalytics = (exp: Experience): Record<string, unknown> => {
    const city = exp.geo.city?.[0];
    const country = exp.geo.country?.[0];

    return {
      city_id: city?.id,
      city_name: city?.name_ru ?? city?.name,
      country_id: country?.id,
      country_name: country?.name,
      discount_rate: exp.discount?.value ?? exp.price.discount?.value,
      duration: exp.duration,
      experience_id: exp.id,
      format: exp.format,
      full_price: exp.price.value,
      guide_id: exp.guide.id,
      is_paid_control: exp.is_paid,
      instant_booking: exp.is_instant_booking,
      rating: exp.rating,
      status: exp.status,
      title: exp.title,
      type: exp.type,
    };
  };

  const onCardClick = (exp: Experience, event: MouseEvent) => {
    const target = event.target as HTMLElement | null;

    // Клик по сердечку избранного отслеживается отдельно
    if (target?.closest('[data-test="favorite"], .favorite')) return;

    if (target?.closest('.guide, .guide-pic, a[href*="/guide/"]')) {
      void trackEvent('around:sights:sight-popup:experience:guide.click', expAnalytics(exp));
      return;
    }

    void trackEvent('around:sights:sight-popup:experience.click', expAnalytics(exp));
  };

  const onToggleFavorite = (exp: Experience) => {
    const wasFavorite = isFavorite(exp.id);

    void trackEvent('around:sights:sight-popup:experience:add-to-favorite.click', {
      ...expAnalytics(exp),
      action: wasFavorite ? 'remove' : 'add',
    });

    void toggleFavorite(exp.id);
  };

  const localizedExperiences = computed<Experience[]>(() =>
    props.experiences.map((exp) => localizeExperienceMovement(exp)),
  );

  const BOTTOM_THRESHOLD = 100;

  const isStuck = ref(false);
  const shadowRoot = inject<ShadowRoot | null>('shadowRoot', null);

  let modalScrollEl: HTMLElement | null = null;

  const onModalScroll = () => {
    if (!modalScrollEl) return;

    const { scrollTop, scrollHeight, clientHeight } = modalScrollEl;

    isStuck.value = scrollHeight - scrollTop - clientHeight > BOTTOM_THRESHOLD;
  };

  const initScrollListener = () => {
    if (modalScrollEl) return;

    modalScrollEl =
      shadowRoot?.querySelector<HTMLElement>('.ui-modal__scroll-container') ??
      document.querySelector<HTMLElement>('.ui-modal__scroll-container');

    if (!modalScrollEl) return;

    modalScrollEl.addEventListener('scroll', onModalScroll, { passive: true });
    onModalScroll();
  };

  const stopScrollListener = () => {
    modalScrollEl?.removeEventListener('scroll', onModalScroll);
    modalScrollEl = null;
  };

  const pluralize = (count: number, [one, few, many]: [string, string, string]) => {
    const mod10 = count % 10;
    const mod100 = count % 100;

    if (mod10 === 1 && mod100 !== 11) return one;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;

    return many;
  };

  const btnText = computed(() => {
    const count = props.totalCount;

    return count
      ? `Показать ${String(count)} ${pluralize(count, ['экскурсию', 'экскурсии', 'экскурсий'])}`
      : 'Все предложения';
  });

  const onShowAll = () => {
    void trackEvent('around:sights:sight-popup:show-all-experiences.click', {
      ...props.analyticsBase,
      experience_count: props.totalCount,
    });

    if (props.showAllUrl) {
      window.open(props.showAllUrl, '_blank');
      return;
    }

    emit('show-all');
  };

  watch(
    () => props.experiences.length,
    async (len) => {
      if (len > 0) {
        seedFavorites(props.experiences);
        await nextTick();
        initScrollListener();
      }
    },
    { immediate: true },
  );

  onBeforeUnmount(stopScrollListener);
</script>

<style lang="scss" scoped>
  .experience-feed {
    width: 100%;
    margin-top: var(--alias-space-48);
  }

  .feed-header {
    margin: 0;

    @include base-600-500-extra;
  }

  .feed-list {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: var(--alias-space-16);
    margin-top: var(--alias-space-16);

    @include mobile {
      grid-template-columns: 1fr;
      row-gap: var(--alias-space-24);
    }
  }

  .feed-actions-wrap {
    min-height: 88px;
    padding-top: var(--alias-space-16);
  }

  .feed-actions {
    &.is-stuck {
      position: fixed;
      bottom: 0;
      left: 50%;
      z-index: 10;
      display: flex;
      justify-content: center;
      width: var(--modal-wrapper-width, 100%);
      padding: var(--alias-space-16);
      background: linear-gradient(180deg, rgb(255 255 255 / 0%) 0%, var(--alias-color-background-default) 100%);
      transform: translateX(-50%);
    }

    @include tablet {
      justify-content: stretch;
    }
  }

  .show-more-button {
    width: 100%;

    .feed-actions.is-stuck & {
      width: auto;
      min-width: 280px;
    }
  }
</style>
